import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/api";
import { DOSSIER_STATUSES, FISCAL_YEARS, EXPENSE_CATEGORIES, CLIENT_TYPES } from "@/constants/modules";
import { useFiscalYear } from "@/contexts/FiscalYearContext";
import {
  BarChart3, Users, FileText, CheckCircle2, Clock, Receipt, DollarSign,
  Wallet, TrendingUp, GitCompare,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import YearSummaryTab from "./expenses/YearSummaryTab.jsx";

const DEFAULT_YEAR = FISCAL_YEARS[0] || new Date().getFullYear() - 1;

// Batch #14.116 — Résumé annuel fusionné depuis Dépenses cabinet
const TABS = [
  { id: "production", label: "Production", icon: BarChart3 },
  { id: "financial", label: "Financier", icon: TrendingUp },
  { id: "summary", label: "Résumé annuel", icon: Receipt },
];

export default function TaxReportsModule() {
  const { year: globalYear, setYear: setGlobalYear } = useFiscalYear();
  const [tab, setTab] = useState("production");
  // Iter74: hydrate from global default; Reports tabs need a concrete year.
  const [year, setYearLocal] = useState(globalYear || DEFAULT_YEAR);
  const [clientType, setClientType] = useState("");
  const [compareYear, setCompareYear] = useState("");

  const setYear = (y) => { setYearLocal(y); setGlobalYear(y); };

  const [production, setProduction] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  // Lazy-load per tab to reduce backend pressure
  const loadTab = useCallback(async (t) => {
    const params = {};
    if (year) params.tax_year = year;
    if (clientType) params.client_type = clientType;
    setLoading(true);
    try {
      if (t === "production") {
        const r = await api.get("/reports/production-summary", { params });
        setProduction(r.data);
      } else if (t === "financial") {
        const r = await api.get("/dashboard/summary", { params });
        setFinancial(r.data);
      }
    } catch {
      // On error, reset the tab's data to avoid displaying stale data
      if (t === "production") setProduction(null);
      else if (t === "financial") setFinancial(null);
    }
    finally { setLoading(false); }
  }, [year, clientType]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const loadComparison = useCallback(async () => {
    if (!compareYear) { setComparison(null); return; }
    const years = [compareYear, year].filter(Boolean).join(",");
    try {
      const r = await api.get("/reports/year-comparison", { params: { years, ...(clientType ? { client_type: clientType } : {}) } });
      setComparison(r.data);
    } catch { setComparison(null); }
  }, [compareYear, year, clientType]);

  useEffect(() => { loadComparison(); }, [loadComparison]);

  const chartData = useMemo(() => (financial?.monthly_revenue || []).map(m => ({
    month: (m.month || "").slice(5),
    Revenus: m.revenue,
    Depenses: m.expenses,
  })), [financial]);

  const expCats = useMemo(
    () => Object.entries(financial?.expense_by_category || {}).sort((a, b) => b[1] - a[1]),
    [financial],
  );
  const getCatLabel = (id) => EXPENSE_CATEGORIES.find(c => c.id === id)?.label || id;

  return (
    <div className="max-w-6xl mx-auto" data-testid="tax-reports-module">
      {/* Header + filters bar */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Rapports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vue d'ensemble — Année fiscale {year || "Toutes"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={year || ""} onChange={e => setYear(e.target.value ? parseInt(e.target.value) : null)}
            className="border rounded-lg px-3 py-2 text-sm" data-testid="report-year-select">
            <option value="">Toutes les années fiscales</option>
            {FISCAL_YEARS.map(y => <option key={y} value={y}>{`Année fiscale ${y}`}</option>)}
          </select>
          <select value={clientType} onChange={e => setClientType(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm" data-testid="report-client-type-select">
            <option value="">Tous les types</option>
            {CLIENT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <div className="flex items-center gap-1 border rounded-lg px-2 py-1 text-sm" title="Comparer deux années fiscales">
            <GitCompare className="w-3.5 h-3.5 text-purple-500" />
            <select value={compareYear} onChange={e => setCompareYear(e.target.value ? parseInt(e.target.value) : "")}
              className="border-0 bg-transparent outline-none text-xs py-1" data-testid="report-compare-year">
              <option value="">vs.</option>
              {FISCAL_YEARS.filter(y => y !== year).map(y => <option key={y} value={y}>{`Année ${y}`}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-4 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition whitespace-nowrap ${tab === t.id ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            data-testid={`report-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="px-4 py-10 text-center text-gray-400" data-testid="report-loading">
          <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto" />
        </div>
      )}

      {/* ── PRODUCTION TAB ─────────────────────────────────────────── */}
      {tab === "production" && production && !loading && (
        <div data-testid="report-production-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total clients" value={production.total_clients} icon={Users} color="blue" />
            <StatCard label="Termines" value={(production.by_status?.termine || 0) + (production.by_status?.transmis || 0) + (production.by_status?.ferme || 0)}
              icon={CheckCircle2} color="green" />
            <StatCard label="En cours" value={production.by_status?.en_cours || 0}
              icon={Clock} color="indigo" />
            <StatCard label="En attente" value={production.by_status?.en_attente_documents || 0}
              icon={FileText} color="yellow" />
          </div>

          <div className="bg-white border rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Repartition par statut</h3>
              <span className="text-xs text-gray-400">Cliquer pour filtrer la liste</span>
            </div>
            <div className="space-y-2">
              {DOSSIER_STATUSES.map(s => {
                const count = production.by_status?.[s.id] || 0;
                const pct = production.total_clients > 0 ? (count / production.total_clients) * 100 : 0;
                return (
                  <div key={s.id}
                    className="w-full flex items-center gap-3 rounded-lg px-2 py-1 text-left"
                    data-testid={`status-row-${s.id}`}>
                    <span className="text-xs w-28 text-gray-600">{s.label}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${s.color.split(" ")[0]}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Client type breakdown */}
          {Object.keys(production.by_client_type || {}).length > 0 && (
            <div className="bg-white border rounded-xl p-5 mb-4" data-testid="by-client-type">
              <h3 className="font-semibold text-sm mb-3">Repartition par type de dossier</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(production.by_client_type).map(([type, count]) => {
                  const label = CLIENT_TYPES.find(t => t.id === type)?.label || type;
                  return (
                    <button key={type} onClick={() => setClientType(type)}
                      className="border rounded-lg px-3 py-2 text-left hover:bg-blue-50 transition">
                      <p className="text-lg font-bold text-gray-900">{count}</p>
                      <p className="text-xs text-gray-500 truncate">{label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Year comparison */}
          {comparison && comparison.data?.length === 2 && (
            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl p-5" data-testid="year-comparison">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <GitCompare className="w-4 h-4 text-purple-600" />
                Comparaison Année fiscale {comparison.data[0].tax_year} vs {comparison.data[1].tax_year}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {comparison.data.map(d => (
                  <div key={d.tax_year} className="bg-white border rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-medium">Année fiscale {d.tax_year}</p>
                    <p className="text-2xl font-bold">{d.total_clients}</p>
                    <p className="text-xs text-gray-500">clients — {d.completion_rate}% termines</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── FINANCIAL TAB ─────────────────────────────────────────── */}
      {tab === "financial" && financial && !loading && (
        <div className="space-y-4" data-testid="reports-financial-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Revenus" value={`${(financial.total_invoiced || 0).toFixed(2)} $`} icon={Receipt} color="blue" />
            <StatCard label="Depenses" value={`${(financial.total_expenses || 0).toFixed(2)} $`} icon={Wallet} color="red" />
            <StatCard label="Benefice net" value={`${(financial.net_profit || 0).toFixed(2)} $`} icon={DollarSign} color={(financial.net_profit || 0) >= 0 ? "green" : "red"} />
            <StatCard label="Depenses deductibles" value={`${(financial.deductible_expenses || 0).toFixed(2)} $`} icon={CheckCircle2} color="indigo" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border rounded-xl p-4" data-testid="reports-monthly-chart">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" /> Revenus vs Depenses par mois</h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={60} />
                    <Tooltip formatter={(v) => `${v.toFixed(2)} $`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Revenus" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Depenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">Aucune donnee financiere</div>
              )}
            </div>

            <div className="bg-white border rounded-xl overflow-hidden" data-testid="reports-expense-by-category">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Wallet className="w-4 h-4 text-red-500" /> Depenses par categorie</h3>
              </div>
              <div className="divide-y max-h-[280px] overflow-y-auto">
                {expCats.map(([cat, amount]) => (
                  <div key={cat} className="px-4 py-2 flex items-center justify-between">
                    <span className="text-sm text-gray-700">{getCatLabel(cat)}</span>
                    <span className="text-sm font-medium text-red-600">{amount.toFixed(2)} $</span>
                  </div>
                ))}
                {!expCats.length && <div className="px-4 py-6 text-center text-gray-400 text-sm">Aucune depense</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch #14.116 — SUMMARY TAB (Résumé annuel fusionné depuis Dépenses cabinet) */}
      {tab === "summary" && (
        <div data-testid="reports-summary-tab">
          <YearSummaryTab year={year} />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, onClick }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    indigo: "bg-indigo-50 text-indigo-600",
    yellow: "bg-yellow-50 text-yellow-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
  };
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper onClick={onClick}
      className={`bg-white border rounded-xl p-4 text-left ${onClick ? "hover:shadow-sm hover:border-blue-200 transition cursor-pointer w-full" : ""}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </Wrapper>
  );
}
